/* global getComputedStyle, document */
import { useMemo } from 'react';

import { useTheme } from '../../theme/useTheme.js';

const TOKENS = {
  axis: '--chart-axis',
  grid: '--chart-grid',
  revenue: '--chart-revenue',
  revenueFill: '--chart-revenue-fill',
  expense: '--chart-expense',
  expenseFill: '--chart-expense-fill',
  // Dedicated chart accent: in the light "paper" theme the UI accent is
  // black, but data viz keeps the blue colour world of the dark theme.
  accent: '--chart-accent',
};

/**
 * Pure reader: pull chart-relevant CSS variables off a CSSStyleDeclaration-like
 * object. chart.js needs concrete colour strings, not `var()` refs.
 */
export function readChartPalette(styles) {
  const read = (name) => styles.getPropertyValue(name).trim();
  const palette = {};
  for (const [key, varName] of Object.entries(TOKENS)) {
    palette[key] = read(varName);
  }
  palette.accentFill = palette.revenueFill;
  // Categorical series for donuts / multi-series bars.
  palette.series = [
    read('--chart-accent'),
    read('--color-success'),
    read('--color-warning'),
    read('--color-danger'),
    read('--color-info'),
    read('--chart-expense'),
  ];
  return palette;
}

/**
 * Live palette that re-reads whenever the active theme flips, so chart colours
 * track the CSS variables in both light and dark.
 */
export function useChartPalette() {
  const { activeTheme } = useTheme();
  return useMemo(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    return readChartPalette(getComputedStyle(document.documentElement));
  }, [activeTheme]);
}
